"use client";

import { ChevronRight, User, Settings, Calendar, Store, CreditCard, Printer, Globe, Zap } from "lucide-react";
import { cn } from "../../lib/utils";
import { useNavigate, useLocation } from "react-router-dom";

const settingsTabs = [
  { id: "profile", label: "Profile", path: "/aesthetics/settings/profile", icon: User, description: "Account info" },
  { id: "general", label: "General", path: "/aesthetics/settings/general", icon: Settings, description: "Business details" },
  { id: "bookings", label: "Appointments", path: "/aesthetics/settings/bookings", icon: Calendar, description: "Booking hours" },
  { id: "vendor-page", label: "Business Page", path: "/aesthetics/settings/vendor-page", icon: Store, description: "Public profile" },
  { id: "quick-payments", label: "Payments", path: "/aesthetics/settings/quick-payments", icon: CreditCard, description: "Payment options" },
  { id: "attach-domain", label: "Domain", path: "/aesthetics/settings/attach-domain", icon: Globe, description: "Custom domain" },
  { id: "xonder", label: "XONDER", path: "/aesthetics/settings/xonder", icon: Zap, description: "Integrations" },
  { id: "printer", label: "Printer", path: "/aesthetics/settings/printer", icon: Printer, description: "Receipt settings" },
];

export default function AestheticSettingsSubSidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="relative w-full lg:w-64 lg:ml-2 lg:mt-3">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Sidebar Header */}
          <div className="bg-gradient-to-r from-[#885ABB] to-[#9D6FD1] px-4 py-3">
            <h3 className="text-sm font-semibold text-white tracking-wide">Settings</h3>
            <p className="text-xs text-white/70 mt-0.5">Manage your preferences</p>
          </div>
          
          {/* Sidebar Items */}
          <div className="py-2">
            {settingsTabs.map((item) => {
              const isActive = location.pathname === item.path;
              const IconComponent = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-200 group relative",
                    isActive
                      ? "bg-purple-50 text-[#885ABB]"
                      : "text-gray-600 hover:bg-gray-50 hover:text-[#885ABB]"
                  )}
                >
                  {/* Active Indicator */}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-[#885ABB] rounded-r-full" />
                  )}
                  
                  {/* Icon */}
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                    isActive 
                      ? "bg-[#885ABB] text-white" 
                      : "bg-gray-100 text-gray-500 group-hover:bg-purple-100 group-hover:text-[#885ABB]"
                  )}>
                    <IconComponent size={16} />
                  </div>
                  
                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <span className={cn(
                      "block text-sm font-medium truncate",
                      isActive ? "text-[#885ABB]" : "text-gray-700 group-hover:text-[#885ABB]"
                    )}>
                      {item.label}
                    </span>
                    <span className="block text-xs text-gray-400 truncate">
                      {item.description}
                    </span>
                  </div>
                  
                  {/* Arrow */}
                  <ChevronRight
                    className={cn(
                      "w-4 h-4 transition-all duration-200",
                      isActive
                        ? "opacity-100 text-[#885ABB]"
                        : "opacity-0 group-hover:opacity-100 text-gray-400"
                    )}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile Horizontal Tabs */}
      <div className="lg:hidden">
        <div className="flex flex-row overflow-x-auto w-full bg-white py-2 border-b scrollbar-hide relative">
          {settingsTabs.map((item) => {
            const isActive = location.pathname === item.path;
            const IconComponent = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-all duration-200",
                  isActive
                    ? "text-[#885ABB] border-b-2 border-[#885ABB]"
                    : "text-gray-600 hover:text-[#885ABB]"
                )}
              >
                <IconComponent size={16} />
                {item.label}
              </button>
            );
          })}
          
          {/* Right side fade for mobile scroll */}
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
        </div>
      </div>
    </div>
  );
}